import type { DataSourceBase, Environment, PageDataVariableWidthRecord, RouteParameterWithValue } from '@lighthouse/core'
import type { AiModule, FlowRunStatus } from '@lighthouse/shared'
import { getPagePathParams, getPageQueryParams, spaceGradeCodeMap, spaceGradeUploadLimitMap, useAtomData } from '@lighthouse/shared'
import { useSetAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { find, includes } from 'rambda'
import { useCallback, useMemo, useState } from 'react'
import { useAsyncRetry } from 'react-use'
import useSWR from 'swr'

import { CURRENT_PAGE, HOME_PAGE } from '@/atoms/application/constants'
import { currentAppOfSpaceIdAtom, otherPlatformListAtom, sharePageAtom } from '@/atoms/application/state'
import {
    homePageAtom,
    languagePageListAtom,
    lastPageOfStackAtom,
    pageStackAtomFamily,
    pageVariableRecordFamily
} from '@/atoms/page/state'
import { workSpaceAtom } from '@/atoms/workSpace/state'
import { AI_INTEGRATION_TYPE_LIST } from '@/containers/OtherPlatforms/constant'
import type { TemplateList } from '@/containers/Template/types'
import { useCurrentSpaceID } from '@/hooks/useWorkSpace'
import * as srv from '@/services'
import type { GetWorkflowRunLogsPayload, RunDev } from '@/services/types'

/**
 * 多个组件内共享同一个局部数据请求方法
 * 获取空间成员列表
 * @export
 * @param {string} spaceId
 * @return {*}
 */
export function useSpaceUserList(spaceId: string, keyword?: string) {
    const { data, error, isLoading, mutate } = useSWR(`/spaceGetUsers/get-${spaceId}`, () => srv.getSpaceUsers(spaceId, keyword), {
        revalidateOnFocus: false
    })

    return { data, error, isLoading, update: mutate }
}

/**
 * @description 获取模板列表
 * @export
 * @return {*}
 */
export function useTemplateList() {
    const { data, mutate } = useSWR<TemplateList>(
        `/app/getTemplateList/`,
        async () => {
            const data = await srv.getTemplateList()
            return data.filter(kind => kind.list.length)
        },
        {
            revalidateOnFocus: false,
            keepPreviousData: true
        }
    )
    return { data, update: mutate }
}

/**
 * @description 获取数据源列表
 * @export
 * @return {*}
 */
export function useEnvList(appId: string) {
    const { data, mutate } = useSWR<Environment[]>(`${appId}-listDs`, () => (appId ? srv.getAppListEnv(appId) : Promise.resolve([])), {
        revalidateOnFocus: false,
        keepPreviousData: true
    })
    return { data, update: mutate }
}

/**
 * @description 获取数据源列表
 * @export
 * @return {*}
 */
export function useDataSourceList(appId: string, envId: string) {
    const { data, mutate } = useSWR<DataSourceBase[]>(
        `${appId}-${envId}-listDs`,
        () => (appId && envId ? srv.listDs(appId, envId) : Promise.resolve([])),
        {
            revalidateOnFocus: false,
            keepPreviousData: true
        }
    )
    return { data, update: mutate }
}

/**
 * @description 获取空间配额及空间信息
 * @export
 * @return {*}
 */
export function useSpaceQuota() {
    const appOfSpaceId = useAtomData(currentAppOfSpaceIdAtom)
    const spaceId = useCurrentSpaceID()
    const setWorkSpace = useSetAtom(workSpaceAtom)
    const currentSpaceId = spaceId || appOfSpaceId
    const { data, mutate } = useSWR(
        `/space/getUsage-${currentSpaceId}`,
        async () => {
            if (!currentSpaceId) {
                return
            }
            const res = await srv.getUsage(currentSpaceId)
            setWorkSpace(draft => {
                draft.expiredAt = res.expiredAt
                draft.version = res.currentVersion
            })
            return {
                ...res,
                spaceId: currentSpaceId,
                currentVersionCode: spaceGradeCodeMap[res.currentVersion],
                uploadFileLimitSize: spaceGradeUploadLimitMap[res.currentVersion]
            }
        },
        {
            revalidateOnFocus: false,
            keepPreviousData: true
        }
    )
    return { data, update: mutate }
}

/**
 * @description 获取应用分析数据
 * @returns
 */
export function useGetAnalyzeConfigure() {
    return useSWR('fetchAnalysisConfigure', () => srv.fetchAnalysisConfigure(), {
        revalidateOnFocus: false,
        keepPreviousData: true
    })
}

export function usePageShare() {
    const getPageInfo = useAtomCallback(
        useCallback(get => {
            const pageId = get(lastPageOfStackAtom)?.pageId || ''
            const stackId = get(lastPageOfStackAtom)?.stackId || ''
            const sharePage = get(sharePageAtom)
            const homePage = get(homePageAtom)
            const pageList = get(languagePageListAtom)
            const { id: homePageId = '' } = homePage ?? {}

            if (sharePage === CURRENT_PAGE || (sharePage === HOME_PAGE && homePage?.id === pageId) || pageId === sharePage) {
                const page = find(item => item.id === pageId, pageList)
                if (!page || !page.parameterAndVariable) {
                    return { pageId }
                }
                const { pathParameter, queryParameter, variables } = page.parameterAndVariable
                // const info = get(pageVariableValueFamily({ stackId, pageId, id, itemId }))
                const pageDataVariable =
                    variables?.reduce<PageDataVariableWidthRecord[]>((prev, item) => {
                        const info = get(pageVariableRecordFamily({ stackId, variableId: item.id }))
                        if (info) {
                            prev.push({
                                id: item.id,
                                dsId: info.ds.id,
                                recordId: info.record.id
                            })
                        }
                        return prev
                    }, []) || []

                const pathValue = pathParameter?.reduce<RouteParameterWithValue[]>((prev, cur) => {
                    const stack = get(pageStackAtomFamily(stackId))
                    const pagePathParameterValues = getPagePathParams(pathParameter, stack?.pagePathParameters || [])
                    if (pagePathParameterValues) {
                        prev.push({
                            id: cur.id,
                            value: pagePathParameterValues[cur.id]
                        })
                    }
                    return prev
                }, [])

                const queryValue = queryParameter?.reduce<RouteParameterWithValue[]>((prev, cur) => {
                    const stack = get(pageStackAtomFamily(stackId))
                    const pageQueryParameterValues = getPageQueryParams(queryParameter, stack?.pageQueryParameters || {})
                    if (pageQueryParameterValues) {
                        prev.push({
                            id: cur.id,
                            value: pageQueryParameterValues[cur.id]
                        })
                    }
                    return prev
                }, [])

                return {
                    pageId,
                    pageDataVariable,
                    pathParameter: pathValue,
                    queryParameter: queryValue
                }
            }

            if (sharePage === HOME_PAGE) {
                return {
                    pageId: homePageId
                }
            }

            return {
                pageId: sharePage
            }
        }, [])
    )

    const pageInfo = useMemo(() => getPageInfo(), [getPageInfo])

    const { value: data, retry } = useAsyncRetry(() => {
        return pageInfo.pageId ? srv.getWechatShareData(pageInfo) : Promise.resolve(undefined)
    })

    return { data, update: retry }
}

export type FlowRunStatusTab = 'all' | FlowRunStatus
export type RunType = 'all' | RunDev

export type FlowLogsWithPaginationParams = {
    workflowId?: GetWorkflowRunLogsPayload['workflowId']
    status?: FlowRunStatusTab
    envId?: string
    runEnv?: RunType
}

export function useFlowLogsWithPagination(params: FlowLogsWithPaginationParams) {
    const { workflowId, status, envId, runEnv } = params
    const workflowStatus = status === 'all' ? undefined : status
    const workflowEnvId = envId === 'all' ? undefined : envId
    const workflowRunEnv = runEnv === 'all' ? undefined : runEnv
    const [currentPage, setCurrentPage] = useState(1)

    const pageSize = 50

    const { data } = useSWR(`flowRunLogs/${workflowId}/${workflowStatus}/${workflowEnvId}/${workflowRunEnv}/${currentPage}`, async () => {
        const res = await srv.fetchFlowRunLog({
            workflowId,
            status: workflowStatus,
            runEnv: workflowRunEnv,
            envId: workflowEnvId,
            currentPage,
            pageSize
        })
        return res.content
    })
    const { list = [], total = 0 } = data || {}

    const totalPage = Math.ceil(total / pageSize)

    return {
        logs: list,
        total,
        totalPage,
        currentPage,
        setCurrentPage
    }
}

/**
 * 获取阿里云短信配置信息
 *
 * @export
 * @param {string} aliyunAccountId
 * @returns {*}
 */
export function useGetAliyunMessageInfo(aliyunAccountId: string | undefined) {
    return useSWR(
        aliyunAccountId,
        (id: string | undefined) => {
            if (!id) {
                return
            }

            return srv.getAliyunMessageInfo({ id })
        },
        {
            revalidateOnFocus: false
        }
    )
}

/**
 * 获取微信消息模版信息
 *
 * @export
 * @param {string} aliyunAccountId
 * @returns {*}
 */
export function useGetWxMessageInfo(appId: string) {
    return useSWR(`wxTemplateMessage_${appId}`, () => {
        return srv.getWxMessageInfo()
    })
}

/**
 * 获取微信小程序消息模版信息
 *
 * @export
 * @param {string} aliyunAccountId
 * @returns {*}
 */
export function useGetWxMiniProgramMessageInfo(appId: string, disable?: boolean) {
    return useSWR(`miniProgramTemplateMessage_${appId}`, () => {
        if (disable) {
            return []
        }
        return srv.getWxMiniProgramMessageInfo()
    })
}

export function useGetWechatDeliveryList(appId: string, disable?: boolean) {
    return useSWR(
        disable ? null : `wechatDeliveryList_${appId}`,
        () => srv.getWechatDeliveryList(),
        {
            revalidateOnFocus: false,
            shouldRetryOnError: false
        }
    )
}
/**
 *
 * @returns 获取微信小程序审核信息
 */
export function useWxMiniProgramsAuthorizationResult() {
    const { data, mutate, isLoading } = useSWR(
        'getWxMiniProgramsAuthorizationResult',
        async () => {
            const { authStatus: authorizationStatus, errorContent } = await srv.getWxMiniProgramsAuthorizationResult()
            const wxProgramsInfo = await srv.getWxProgramsInfo()
            return { ...wxProgramsInfo, authorizationStatus, error: errorContent }
        },
        {
            revalidateOnFocus: false
        }
    )
    return { data, update: mutate, loading: isLoading }
}

/**
 * @returns 获取集成模型列表
 */

/**
 * @returns 获取集成模型列表
 */
export function useGetAiModelList() {
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    const platforms =
        otherPlatforms
            ?.filter(item => item.configurationComplete && includes(item.type, AI_INTEGRATION_TYPE_LIST))
            ?.map(item => item.type) ?? []
    const key = platforms && platforms.length > 0 ? String(platforms) : 'all'

    const { data, mutate, isLoading } = useSWR(
        key,
        () => {
            return srv.getAiModelList()
        },
        {
            revalidateOnFocus: false,
            keepPreviousData: true,
            revalidateIfStale: false // 不在 stale 状态下自动重新获取
        }
    )

    // if (!data) {
    //     const aiList: AiModule[] = [{
    //         modules: [
    //             {
    //                 label: "qwen-max",
    //                 value: "SYSTEM_QWEN_MAX"
    //             }
    //         ],
    //         system: true,
    //         configurationComplete: true,
    //         type: "QWEN"
    //     }]
    //     return {
    //         data: aiList,
    //         update: mutate
    //     }
    // }

    const aiModelList = data?.reduce<AiModule[]>((prev, cur) => {
        const ai = find(p => p.type === cur.type, otherPlatforms || [])
        if (cur?.type === 'QWEN') {
            const isSystem = cur.modules.some(module => module.value === 'SYSTEM_QWEN_MAX')
            if (isSystem) {
                prev.push({
                    ...cur,
                    configurationComplete: true
                })
                return prev
            }
        }
        if (ai) {
            prev.push({
                ...cur,
                configurationComplete: ai?.configurationComplete
            })
        }
        return prev
    }, [])

    return { data: aiModelList, update: mutate }
}
